'use client';

import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { CELL_PADDING, COL_GAP } from './constants';
import { GridCell } from './types';

/**
 * SVG overlay drawing parent→sire/dam lines. Y-fork when both parents exist,
 * single diagonal otherwise. `pointer-events: none` so card hovers/clicks
 * pass through.
 */
export default function ConnectorLines({
  cells,
  colWidth,
  maxDepth,
  rowHeightPx,
  treeHeight,
}: {
  cells: GridCell[];
  colWidth: number;
  maxDepth: number;
  rowHeightPx: number;
  treeHeight: number;
}) {
  const theme = useTheme();
  const sireColor = theme.palette.info.main;
  const damColor = theme.palette.error.main;
  const connectorColor = theme.palette.text.secondary;
  const svgWidth = maxDepth * colWidth + (maxDepth - 1) * COL_GAP;

  const segments = useMemo(() => {
    const hasNode = new Set(
      cells
        .filter((cell) => cell.node !== null)
        .map((cell) => `${cell.col},${cell.row}`),
    );

    const result: {
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      color: string;
    }[] = [];

    for (const cell of cells) {
      if (cell.depth >= maxDepth - 1) continue;
      if (cell.node === null) continue;

      const depth = cell.depth;
      const half = cell.rowSpan / 2;

      const hasSire = hasNode.has(`${depth + 2},${cell.row}`);
      const hasDam = hasNode.has(`${depth + 2},${cell.row + half}`);
      if (!hasSire && !hasDam) continue;

      const parentRightX =
        depth * (colWidth + COL_GAP) + colWidth - CELL_PADDING;
      const childLeftX = (depth + 1) * (colWidth + COL_GAP) + CELL_PADDING;
      const branchX = (parentRightX + childLeftX) / 2;
      const parentCenterY = (cell.row - 1 + cell.rowSpan / 2) * rowHeightPx;
      const sireCenterY = (cell.row - 1 + half / 2) * rowHeightPx;
      const damCenterY = (cell.row - 1 + half + half / 2) * rowHeightPx;

      if (hasSire && hasDam) {
        // Full Y-branch:
        //   parent ──── branch
        //               │
        //               ├──── sire
        //               │
        //               └──── dam
        result.push({
          x1: parentRightX,
          y1: parentCenterY,
          x2: branchX,
          y2: parentCenterY,
          color: connectorColor,
        });
        result.push({
          x1: branchX,
          y1: sireCenterY,
          x2: branchX,
          y2: parentCenterY,
          color: sireColor,
        });
        result.push({
          x1: branchX,
          y1: parentCenterY,
          x2: branchX,
          y2: damCenterY,
          color: damColor,
        });
        result.push({
          x1: branchX,
          y1: sireCenterY,
          x2: childLeftX,
          y2: sireCenterY,
          color: sireColor,
        });
        result.push({
          x1: branchX,
          y1: damCenterY,
          x2: childLeftX,
          y2: damCenterY,
          color: damColor,
        });
      } else if (hasSire) {
        result.push({
          x1: parentRightX,
          y1: parentCenterY,
          x2: childLeftX,
          y2: sireCenterY,
          color: sireColor,
        });
      } else {
        result.push({
          x1: parentRightX,
          y1: parentCenterY,
          x2: childLeftX,
          y2: damCenterY,
          color: damColor,
        });
      }
    }
    return result;
  }, [
    cells,
    colWidth,
    maxDepth,
    rowHeightPx,
    sireColor,
    damColor,
    connectorColor,
  ]);

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: svgWidth,
        height: treeHeight,
        pointerEvents: 'none',
        overflow: 'visible',
      }}
    >
      {segments.map((segment, index) => (
        <line
          key={index}
          x1={segment.x1}
          y1={segment.y1}
          x2={segment.x2}
          y2={segment.y2}
          stroke={segment.color}
          strokeWidth={1.5}
        />
      ))}
    </svg>
  );
}
